import React, { useEffect, useRef, useState } from 'react';

import AlertCaution from '@rover/icons/svg/alert-caution.svg';
import AlertError from '@rover/icons/svg/alert-error.svg';
import AlertInfo from '@rover/icons/svg/alert-info.svg';
import ReferSocial from '@rover/icons/svg/refer-social.svg';
import { Box, Flex, Heading } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import roverTheme from '@rover/kibble/themes/roverTheme';
import useTheme from '@rover/kibble/themes/useTheme';
import Alert from '@rover/react-lib/src/components/notifications/Alert/Alert';
import { useApiFrontendDjangoMessagesList } from '@rover/rsdk/src/apiClient/latest/api-frontend-django-messages-list';
import { LevelTagEnum } from '@rover/rsdk/src/apiClient/latest/model/levelTagEnum';

const ALERT_DISMISS_TIMEOUT = 8000;

const getMessageSeverity = (
  levelTag: LevelTagEnum
): (typeof Alert.Severity)[keyof typeof Alert.Severity] => {
  switch (levelTag) {
    case 'success':
      return Alert.Severity.SUCCESS;
    case 'warning':
      return Alert.Severity.WARNING;
    case 'error':
      return Alert.Severity.DANGER;
    case 'debug':
    case 'info':
    default:
      return Alert.Severity.INFO;
  }
};

const getMessageIcon = (levelTag: LevelTagEnum, theme: typeof roverTheme): JSX.Element => {
  const {
    colors: { border },
  } = theme;
  const defaultProps = { height: 24, width: 24 };
  switch (levelTag) {
    case 'success':
      return <ReferSocial fill={border.success} {...defaultProps} />;
    case 'warning':
      return <AlertCaution fill={border.caution} {...defaultProps} />;
    case 'error':
      return <AlertError fill={border.error} {...defaultProps} />;
    case 'debug':
    case 'info':
    default:
      return <AlertInfo fill={border.highlight} {...defaultProps} />;
  }
};

const DjangoMessages = (): JSX.Element | null => {
  const theme = useTheme();
  const { data: djangoMessages, isLoading } = useApiFrontendDjangoMessagesList();
  const [showError, setShowError] = useState(true);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    closeTimer.current = setTimeout(() => {
      setShowError(false);
    }, ALERT_DISMISS_TIMEOUT);

    return () => {
      if (closeTimer) {
        clearTimeout(closeTimer.current);
      }
    };
  }, []);

  if (
    !showError ||
    isLoading ||
    !djangoMessages ||
    !djangoMessages.results ||
    djangoMessages.results.length === 0
  ) {
    return null;
  }

  return (
    <Box position="fixed" bottom="0" width="100%" zIndex="1050">
      <Section>
        {djangoMessages.results.map((message, msgsIdx) => (
          <Alert
            key={msgsIdx}
            severity={getMessageSeverity(message.levelTag)}
            onDismiss={(): void => setShowError(false)}
          >
            <Flex flexDirection="row" width="100%" alignItems="center">
              {getMessageIcon(message.levelTag, theme)}
              <Heading pl="3x">
                {/* Replace new lines with <br /> */}
                {message.message.split('\n').map((line, msgIdx, arr) => (
                  <React.Fragment key={msgIdx}>
                    {line}
                    {/* Add <br /> only if it's not the last item */}
                    {msgIdx < arr.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Heading>
            </Flex>
          </Alert>
        ))}
      </Section>
    </Box>
  );
};
export default DjangoMessages;
